import { InitPageStrict } from "../index";

declare var App: any;

class ReplenishmentPickView {
  root: HTMLElement;
  markUrl: string;
  unmarkUrl: string;
  quickGrabUrl: string;
  stylesUrl: string;
  refreshUrl: string;
  updateModeUrl: string;
  currency: string;
  displaySettingsUrl: string;
  csrfToken: string;
  viewOnly: boolean;
  grabbing: boolean;
  exporting: boolean;
  refilling: boolean;
  gridHasMore: boolean;
  toastTimer: number;
  GRID_TARGET = 12;
  refreshing: boolean;
  refreshBtn: HTMLButtonElement;
  refreshOriginal: string;
  refreshFallback: number;
  refreshDebounce: number;

  constructor() {
    this.root = document.querySelector(".new-css.layout1") as HTMLElement;
    if (!this.root) return;

    this.markUrl = this.root.getAttribute("data-mark-url");
    this.unmarkUrl = this.root.getAttribute("data-unmark-url");
    this.quickGrabUrl = this.root.getAttribute("data-quick-grab-url");
    this.stylesUrl = this.root.getAttribute("data-styles-url");
    this.refreshUrl = this.root.getAttribute("data-refresh-url");
    this.updateModeUrl = this.root.getAttribute("data-update-mode-url");
    this.displaySettingsUrl = this.root.getAttribute("data-display-settings-url");
    this.viewOnly = this.root.getAttribute("data-view-only") === "true";
    this.currency = this.root.getAttribute("data-currency") || "$";
    this.gridHasMore = this.root.getAttribute("data-has-more") === "true";
    const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    this.csrfToken = meta ? meta.content : "";

    if (!this.viewOnly) {
      this.bindPicks();
      this.bindSelection();
      this.bindRefresh();
    }
    this.bindReplenishmentChannel();
    this.bindLoadMore();
    this.bindSendForm();
    this.bindModals();
    this.bindCategoryFilter();
    this.bindSortDropdown();
    this.bindImageModal();
    this.bindExport();
    this.bindFlashAutoFade();
  }

  // ---- Refresh (background sync + live update) --------------------------

  bindRefresh() {
    const btn = document.getElementById("btnRefresh") as HTMLButtonElement;
    if (!btn) return;
    btn.addEventListener("click", () => {
      if (this.refreshing) return;
      this.refreshing = true;
      this.refreshBtn = btn;
      this.refreshOriginal = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Syncing…';

      fetch(this.refreshUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Accept": "application/json" },
        credentials: "same-origin"
      }).catch(() => {});

      // fallback in case no broadcast arrives (e.g. cable disconnected)
      this.refreshFallback = window.setTimeout(() => this.completeRefresh(), 30000);
    });
  }

  bindReplenishmentChannel() {
    if (typeof App === "undefined" || !App || !App.cable) return;
    const tenant = this.root.getAttribute("data-tenant-code");
    App.cable.subscriptions.create(
      { channel: "Alerts::ReplenishmentUpdatesChannel", tenant_code: tenant },
      { received: (data: any) => this.onReplenishmentUpdate(data) }
    );
  }

  // The sync broadcasts after each step (create/update/resolve); only the final
  // resolve step means the data is complete. Untagged broadcasts fall back to
  // the debounce so unknown sources still complete a pending refresh.
  onReplenishmentUpdate(data: any) {
    if (!this.refreshing) return; // only react to a refresh the user started
    if (data && data.step && data.step !== "resolve") return;
    if (this.refreshDebounce) clearTimeout(this.refreshDebounce);
    this.refreshDebounce = window.setTimeout(() => this.completeRefresh(), 900);
  }

  completeRefresh() {
    if (!this.refreshing) return;
    this.refreshing = false;
    if (this.refreshFallback) clearTimeout(this.refreshFallback);
    if (this.refreshDebounce) clearTimeout(this.refreshDebounce);

    const restore = () => {
      if (this.refreshBtn) {
        this.refreshBtn.disabled = false;
        this.refreshBtn.innerHTML = this.refreshOriginal;
      }
      const ts = document.querySelector(".refresh-meta .ts");
      if (ts) ts.textContent = "just now";
    };
    this.reloadRegions().then(
      () => { restore(); this.showToast("✓ Replenishment synced"); },
      () => { restore(); this.showToast("Couldn't sync — please try again"); }
    );
  }

  showToast(message: string) {
    const toast = document.getElementById("toast");
    if (!toast) return;
    toast.innerHTML = "";
    const msg = document.createElement("span");
    msg.textContent = message;
    toast.appendChild(msg);
    toast.classList.add("show");
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => toast.classList.remove("show"), 4000);
  }

  // ---- Export ----------------------------------------------------------

  bindExport() {
    const link = document.getElementById("exportBtn") as HTMLAnchorElement;
    if (!link) return;
    link.addEventListener("click", (e: any) => {
      if (this.exporting) { e.preventDefault(); return; } // prevent multiple clicks
      this.exporting = true;
      const originalHtml = link.innerHTML;
      link.classList.add("disabled");
      link.style.pointerEvents = "none";
      link.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Exporting…';
      // the download proceeds; close the modal and re-enable after a short window
      const modal = document.getElementById("exportModal");
      setTimeout(() => {
        if (modal) modal.classList.remove("open");
        this.exporting = false;
        link.classList.remove("disabled");
        link.style.pointerEvents = "";
        link.innerHTML = originalHtml;
      }, 1200);
    });
  }

  // ---- Flash messages --------------------------------------------------

  bindFlashAutoFade() {
    const flashes = document.querySelectorAll(".alert.alert-success, .alert.alert-danger, .alert.alert-warning");
    Array.prototype.forEach.call(flashes, (el: HTMLElement) => {
      setTimeout(() => {
        el.style.transition = "opacity 0.5s ease, transform 0.5s ease";
        el.style.opacity = "0";
        el.style.transform = "translateY(-8px)";
        setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 500);
      }, 7000);
    });
  }

  // ---- Image lightbox --------------------------------------------------

  bindImageModal() {
    const modal = document.getElementById("pickImageModal");
    const img = document.getElementById("pickModalImage") as HTMLImageElement;
    if (!modal || !img) return;

    const close = () => modal.classList.remove("open");

    // Open on thumbnail click (delegated so AJAX-loaded cards work too)
    this.root.addEventListener("click", (e: any) => {
      const trigger = (e.target as HTMLElement).closest(".modal-image-trigger") as HTMLElement;
      if (!trigger) return;
      const src = trigger.getAttribute("data-original");
      if (!src) return;
      img.src = src;
      img.alt = trigger.getAttribute("data-alt") || "";
      modal.classList.add("open");
    });

    modal.addEventListener("click", (e: any) => {
      const t = e.target as HTMLElement;
      if (t === modal || t.closest("[data-image-close]")) close();
    });

    document.addEventListener("keydown", (e: any) => {
      if ((e.key === "Escape" || e.keyCode === 27) && modal.classList.contains("open")) close();
    });
  }

  // ---- Card selection --------------------------------------------------

  bindSelection() {
    this.root.addEventListener("change", (e: any) => {
      const cb = (e.target as HTMLElement).closest(".card-select-checkbox") as HTMLInputElement;
      if (!cb) return;
      const container = cb.closest(".card, .quickgrab") as HTMLElement;
      if (container) container.classList.toggle("selected", cb.checked);
      this.updateSelectedCount();
      this.updateSendButtonState();
    });
  }

  selectedContainers(): NodeListOf<Element> {
    return document.querySelectorAll(".grid .card.selected, .quickgrab.selected");
  }

  selectedItemNumbers(): string[] {
    const seen: any = {};
    const numbers: string[] = [];
    Array.prototype.forEach.call(this.selectedContainers(), (container: HTMLElement) => {
      Array.prototype.forEach.call(container.querySelectorAll(".size-box"), (b: HTMLElement) => {
        const n = b.getAttribute("data-item-number");
        if (n && !seen[n]) { seen[n] = true; numbers.push(n); }
      });
    });
    return numbers;
  }

  updateSelectedCount() {
    const el = document.getElementById("sendSelectedCount");
    if (el) el.textContent = String(this.selectedContainers().length);
  }

  updateSendButtonState() {
    const btn = document.getElementById("sendToHHBtn") as HTMLButtonElement;
    if (!btn) return;
    const form = document.getElementById("sendToHHForm");
    const mode = form ? (form.querySelector('input[name="all"]:checked') as HTMLInputElement) : null;
    const selectedMode = !mode || mode.value === "selected";
    const nothingSelected = this.selectedContainers().length === 0;
    btn.disabled = selectedMode && nothingSelected;
  }

  // ---- Picking ---------------------------------------------------------

  bindPicks() {
    this.root.addEventListener("click", (e: any) => {
      const target = e.target as HTMLElement;

      const sizeBox = target.closest(".size-box") as HTMLElement;
      if (sizeBox) {
        if (!sizeBox.closest(".card.replenished")) this.handleSizeBox(sizeBox);
        return;
      }

      const markBtn = target.closest(".mark-btn") as HTMLElement;
      if (markBtn) { e.stopPropagation(); this.handleMarkStyle(markBtn); return; }

      const qgBtn = target.closest(".qg-cta") as HTMLElement;
      if (qgBtn) { this.handleMarkGrabbed(); return; }
    });
  }

  // A size needs `data-qty` units. Each click picks one more unit; a full (green)
  // box clicked again clears the whole size back to zero.
  handleSizeBox(box: HTMLElement) {
    if (box.classList.contains("loading")) return; // prevent multiple clicks
    const qty = parseInt(box.getAttribute("data-qty"), 10) || 0;
    if (qty <= 0) return; // nothing to replenish — already satisfied, not a pick target
    const pickedQty = parseInt(box.getAttribute("data-picked-qty"), 10) || 0;

    if (pickedQty >= qty) {
      this.resetSizeBox(box, pickedQty);
    } else {
      this.pickOneUnit(box, qty, pickedQty);
    }
  }

  pickOneUnit(box: HTMLElement, qty: number, pickedQty: number) {
    const willComplete = pickedQty + 1 >= qty;
    const hero = box.closest("#quickgrab") as HTMLElement;
    const card = box.closest(".card") as HTMLElement;
    const willAdvance = !!hero && willComplete && this.wouldCompleteContainer(hero, box);
    const picks = [{
      item_number: box.getAttribute("data-item-number"),
      ext_item_id: box.getAttribute("data-ext-item-id"),
      quantity: 1
    }];

    box.classList.add("loading");

    this.postPicks(this.markUrl, "POST", picks, willAdvance).then((data: any) => {
      box.classList.remove("loading");
      this.setPickedQty(box, pickedQty + 1);

      if (willComplete) {
        if (willAdvance) {
          this.onHeroGrabbed(hero, data, picks);
        } else if (card && !card.classList.contains("replenished") && this.allPicked(card)) {
          this.onCardGrabbed(card, picks);
        }
      }

      this.syncKpis(data);
    }).catch(() => {
      box.classList.remove("loading");
    });
  }

  resetSizeBox(box: HTMLElement, pickedQty: number) {
    const picks = [{
      item_number: box.getAttribute("data-item-number"),
      ext_item_id: box.getAttribute("data-ext-item-id"),
      quantity: pickedQty
    }];

    box.classList.add("loading");

    this.postPicks(this.unmarkUrl, "DELETE", picks).then((data: any) => {
      box.classList.remove("loading");
      this.setPickedQty(box, 0);
      this.syncKpis(data);
    }).catch(() => {
      box.classList.remove("loading");
    });
  }

  handleMarkStyle(btn: HTMLElement) {
    const button = btn as HTMLButtonElement;
    if (button.disabled) return; // prevent multiple clicks
    const card = button.closest(".card") as HTMLElement;
    const boxes = card.querySelectorAll(".size-box:not(.picked)");
    if (boxes.length === 0) return;

    const originalHtml = button.innerHTML;
    const picks = this.picksFromBoxes(boxes);
    button.disabled = true;
    button.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Working…';

    this.postPicks(this.markUrl, "POST", picks).then((data: any) => {
      // reset the button to its label before it's hidden, so an undo shows it correctly
      button.disabled = false;
      button.innerHTML = originalHtml;
      Array.prototype.forEach.call(boxes, (b: HTMLElement) => this.setPicked(b, true));
      this.onCardGrabbed(card, picks);
      this.syncKpis(data);
    }).catch(() => {
      button.disabled = false;
      button.innerHTML = originalHtml;
    });
  }

  handleMarkGrabbed() {
    if (this.grabbing) return; // prevent multiple clicks
    const hero = document.getElementById("quickgrab") as HTMLElement;
    if (!hero) return;

    const boxes = hero.querySelectorAll(".size-box:not(.picked)");
    const btn = document.getElementById("qgMarkBtn") as HTMLButtonElement;
    const originalHtml = btn ? btn.innerHTML : "";
    const picks = this.picksFromBoxes(boxes);

    this.grabbing = true;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Working…';
    }

    this.postPicks(this.markUrl, "POST", picks, true).then((data: any) => {
      Array.prototype.forEach.call(boxes, (b: HTMLElement) => this.setPicked(b, true));
      this.onHeroGrabbed(hero, data, picks);
      this.syncKpis(data);
      this.grabbing = false;
    }).catch(() => {
      if (btn) { btn.disabled = false; btn.innerHTML = originalHtml; }
      this.grabbing = false;
    });
  }

  // A grid card was fully grabbed: mark it replenished, move it to the end,
  // top the grid back up, and offer an undo (of just the picks this grab made).
  onCardGrabbed(card: HTMLElement, picks: any[]) {
    const name = this.textOf(card, ".card-name");
    this.replenishCard(card);
    this.showUndoToast(name, picks, () => this.restoreCard(card, picks));
    this.topUpGrid();
  }

  // The hero was grabbed: swap in the next priority, drop a replenished card for
  // the grabbed style at the end of the list, top up, and offer an undo.
  onHeroGrabbed(hero: HTMLElement, data: any, picks: any[]) {
    const name = this.textOf(hero, ".qg-name");
    this.showNextQuickGrab(data);
    if (data && typeof data.grabbed_card_html === "string") {
      this.appendReplenishedCard(data.grabbed_card_html);
    }
    this.showUndoToast(name, picks, () => this.reloadRegions());
    this.topUpGrid();
  }

  // AJAX "soft reload" of the hero + grid regions (used to undo a hero grab
  // without a full page refresh).
  reloadRegions() {
    const search = window.location.search;
    const gridUrl = this.stylesUrl + search + (search ? "&" : "?") + "full=1";

    return fetch(gridUrl, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      .then((r) => r.json())
      .then((data: any) => {
        const grid = document.getElementById("pickGrid");
        if (grid) grid.innerHTML = data.html || "";
        this.gridHasMore = !!data.has_more;
        this.updateLoadMore();
        return fetch(this.quickGrabUrl + search, { headers: { "Accept": "text/html" }, credentials: "same-origin" })
          .then((r) => r.text())
          .then((html) => {
            this.renderQuickGrab(html);
            this.applyKpis(data.kpis);
          });
      });
  }

  // Overwrite the KPI strip with server-computed values (returned by the
  // full grid reload), so soft reloads keep every counter in sync.
  applyKpis(kpis: any) {
    if (!kpis) { this.refreshKpis({}); return; }
    this.setKpi("sizesSoldOut", kpis.sizes_sold_out);
    this.setKpi("soldOutUnits", kpis.sold_out_units);
    this.setKpi("sizesBelowTemplate", kpis.sizes_below_template);
    this.setKpi("belowUnits", kpis.below_units);
    this.setKpi("replenTotalUnits", kpis.replen_total_units);
    this.setMoneyKpi("replenTotalValue", kpis.replen_total_value);
    this.setKpi("inShipment", kpis.in_shipment);
    this.setStylesTotal(kpis.styles_total);
    this.setKpi("needingAttention", kpis.needing_attention);
    this.setKpi("totalAlerts", kpis.total_alerts);
    this.setKpi("totalStyles", kpis.styles_total);
  }

  syncKpis(data: any) {
    if (data && data.kpis) {
      this.applyKpis(data.kpis);
    } else {
      this.refreshKpis(data || {});
    }
  }

  // A counter can appear both as a KPI card and in the short summary strip, so
  // every element carrying the counter is updated, not just the card.
  kpiElements(id: string): HTMLElement[] {
    const els: HTMLElement[] = [];
    const card = document.getElementById(id);
    if (card) els.push(card);
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-kpi="' + id + '"]'),
      (el: HTMLElement) => els.push(el)
    );
    return els;
  }

  setKpi(id: string, value: any) {
    if (typeof value === "undefined") return;
    this.kpiElements(id).forEach((el) => { el.textContent = String(value); });
  }

  replenishCard(card: HTMLElement) {
    card.classList.add("replenished");
    card.classList.remove("selected");
    const mark = card.querySelector(".mark-btn") as HTMLButtonElement;
    if (mark) mark.style.display = "none";
    const cb = card.querySelector(".card-select-checkbox") as HTMLElement;
    if (cb) cb.style.display = "none";
    if (!card.querySelector(".replenished-badge")) {
      const badge = document.createElement("span");
      badge.className = "replenished-badge";
      badge.textContent = "✓ Replenished";
      card.insertBefore(badge, card.firstChild);
    }
    this.moveCardToEnd(card);
  }

  restoreCard(card: HTMLElement, picks: any[]) {
    card.classList.remove("replenished");
    const badge = card.querySelector(".replenished-badge");
    if (badge && badge.parentNode) badge.parentNode.removeChild(badge);
    const mark = card.querySelector(".mark-btn") as HTMLButtonElement;
    if (mark) { mark.disabled = false; mark.style.display = ""; }
    const cb = card.querySelector(".card-select-checkbox") as HTMLElement;
    if (cb) cb.style.display = "";
    const undone: any = {};
    picks.forEach((pick: any) => {
      const num = pick.item_number;
      undone[num] = (undone[num] || 0) + (parseInt(pick.quantity, 10) || 0);
    });
    Array.prototype.forEach.call(card.querySelectorAll(".size-box"), (b: HTMLElement) => {
      const num = b.getAttribute("data-item-number");
      if (undone[num]) {
        const cur = parseInt(b.getAttribute("data-picked-qty"), 10) || 0;
        this.setPickedQty(b, cur - undone[num]);
      }
    });
    this.moveCardToRank(card);
    this.refreshKpis({});
  }

  // Put a restored card back in its ranked slot among the actionable cards,
  // never in front of a better-ranked one, and always above the replenished block.
  moveCardToRank(card: HTMLElement) {
    const grid = document.getElementById("pickGrid");
    if (!grid) return;

    const rank = parseInt(card.getAttribute("data-rank"), 10) || 0;
    const before = Array.prototype.slice.call(grid.children).find((el: HTMLElement) => {
      if (el === card || !el.classList.contains("card")) return false;
      if (el.classList.contains("replenished")) return true;
      return (parseInt(el.getAttribute("data-rank"), 10) || 0) > rank;
    }) as HTMLElement;

    if (before === card.nextElementSibling && card.parentNode === grid) return;

    this.flipReorder(grid, () => {
      if (before) grid.insertBefore(card, before);
      else grid.appendChild(card);
    });
  }

  appendReplenishedCard(html: string) {
    const grid = document.getElementById("pickGrid");
    if (!grid) return;
    const temp = document.createElement("div");
    temp.innerHTML = html;
    Array.prototype.slice.call(temp.children).forEach((card: HTMLElement) => grid.appendChild(card));
  }

  showUndoToast(name: string, picks: any[], undoAction: () => void) {
    const toast = document.getElementById("toast");
    if (!toast) return;

    toast.innerHTML = "";
    const msg = document.createElement("span");
    msg.textContent = "Marked picked — " + name;
    const undo = document.createElement("button");
    undo.className = "toast-undo";
    undo.textContent = "UNDO";
    toast.appendChild(msg);
    toast.appendChild(undo);
    toast.classList.add("show");

    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => toast.classList.remove("show"), 6000);

    undo.onclick = () => {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      toast.classList.remove("show");
      this.postPicks(this.unmarkUrl, "DELETE", picks).then((data: any) => {
        undoAction();
        this.syncKpis(data);
      }).catch(() => this.showToast("Couldn't undo — please try again"));
    };
  }

  textOf(el: HTMLElement, selector: string): string {
    const node = el.querySelector(selector);
    return node ? (node.textContent || "").trim() : "";
  }

  // Swap the next hero HTML in, fading it in (the grabbed hero stayed visible
  // until now, so there's no empty gap). The new hero must not also appear in
  // the list, so drop its duplicate grid card if present.
  renderQuickGrab(html: string) {
    const wrap = document.getElementById("quickGrabWrap");
    if (!wrap) return;
    wrap.innerHTML = html;
    const next = wrap.querySelector(".quickgrab") as HTMLElement;
    if (!next) return;

    const dup = this.findCard(next.getAttribute("data-ext-item-id"));
    if (dup) this.removeCardFromGrid(dup);

    this.animateHeroEnter(next);
  }

  removeCardFromGrid(card: HTMLElement) {
    const grid = card.closest(".grid") as HTMLElement;
    if (!grid) { if (card.parentNode) card.parentNode.removeChild(card); return; }
    this.flipReorder(grid, () => { if (card.parentNode) card.parentNode.removeChild(card); });
  }

  // Fallback: fetch the next hero if it wasn't returned inline with the mark.
  advanceQuickGrab() {
    fetch(this.quickGrabUrl + (window.location.search || ""), {
      headers: { "Accept": "text/html" },
      credentials: "same-origin"
    })
      .then((r) => r.text())
      .then((html) => this.renderQuickGrab(html));
  }

  // Use the inline hero HTML from the mark response, or fall back to a fetch.
  showNextQuickGrab(data: any) {
    if (data && typeof data.quick_grab_html === "string") {
      this.renderQuickGrab(data.quick_grab_html);
    } else {
      this.advanceQuickGrab();
    }
  }

  animateHeroEnter(hero: HTMLElement) {
    hero.classList.add("qg-enter");
    requestAnimationFrame(() => {
      hero.classList.add("qg-enter-active");
      hero.classList.remove("qg-enter");
      setTimeout(() => hero.classList.remove("qg-enter-active"), 420);
    });
  }

  // FLIP: smoothly animate grid cards to their new positions after a reorder
  flipReorder(grid: HTMLElement, mutate: () => void) {
    const cards = Array.prototype.slice.call(grid.children) as HTMLElement[];
    const firsts = cards.map((c) => c.getBoundingClientRect());

    mutate();

    const movers: HTMLElement[] = [];
    cards.forEach((c, i) => {
      if (c.parentNode !== grid) return; // was removed during the mutation
      const first = firsts[i];
      const last = c.getBoundingClientRect();
      const dx = first.left - last.left;
      const dy = first.top - last.top;
      if (!dx && !dy) return;
      c.style.transition = "none";
      c.style.transform = "translate(" + dx + "px, " + dy + "px)";
      movers.push(c);
    });

    if (movers.length === 0) return;

    void grid.offsetHeight; // force reflow so the inverted positions apply

    movers.forEach((c) => {
      c.style.transition = "transform 420ms cubic-bezier(0.22, 1, 0.36, 1)";
      c.style.transform = "";
    });
    setTimeout(() => {
      movers.forEach((c) => { c.style.transition = ""; c.style.transform = ""; });
    }, 460);
  }

  // FLIP-move a card to the end of the grid (no-op if already last)
  moveCardToEnd(card: HTMLElement) {
    if (!card) return;
    const grid = card.closest(".grid") as HTMLElement;
    if (!grid || grid.lastElementChild === card) return;
    card.style.display = "";
    this.flipReorder(grid, () => grid.appendChild(card));
  }

  findCard(extId: string): HTMLElement {
    const cards = document.querySelectorAll(".grid .card");
    for (let i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute("data-ext-item-id") === extId) return cards[i] as HTMLElement;
    }
    return null;
  }

  // Fully pick or fully clear a box (used by the "mark all" flows and undo).
  setPicked(box: HTMLElement, picked: boolean) {
    const qty = parseInt(box.getAttribute("data-qty"), 10) || 0;
    this.setPickedQty(box, picked ? qty : 0);
  }

  // Set how many units of a size are picked, and reflect it everywhere: the box
  // colour/label/status, the remaining count, and the live KPI counters.
  setPickedQty(box: HTMLElement, n: number) {
    const qty = parseInt(box.getAttribute("data-qty"), 10) || 0;
    const prev = parseInt(box.getAttribute("data-picked-qty"), 10) || 0;
    n = Math.max(0, Math.min(n, qty));
    box.setAttribute("data-picked-qty", String(n));

    const baseStatus = box.getAttribute("data-base-status") || "sold";
    const full = qty > 0 && n >= qty;
    const remaining = Math.max(0, qty - n);

    box.classList.remove("sold", "topup", "picked");
    box.classList.add(full ? "picked" : baseStatus);

    const qtyEl = box.querySelector(".sz-qty");
    if (qtyEl) qtyEl.textContent = full ? "✓" : ("+" + remaining);

    const status = box.querySelector(".sz-status");
    if (status) {
      status.textContent = full
        ? "Picked"
        : (n > 0 ? (n + "/" + qty + " picked") : (baseStatus === "sold" ? "Sold out" : "Low"));
    }

    if (n !== prev) this.adjustNeedKpis(prev, n, baseStatus, qty, this.retailPrice(box));

    if (n > prev) {
      box.classList.remove("sz-pop");
      void box.offsetWidth; // restart the animation if already played
      box.classList.add("sz-pop");
      setTimeout(() => box.classList.remove("sz-pop"), 340);
    }
  }

  // Live-adjust the "Sizes Sold Out" / "Below Template" counters (and their unit
  // sub-totals) as units are picked/unpicked. Unit sub-totals move by the unit
  // delta; the size counter only moves when the size flips fully-picked.
  adjustNeedKpis(prev: number, n: number, baseStatus: string, qty: number, price: number) {
    const unitDelta = n - prev;
    if (unitDelta === 0) return;

    const wasFull = qty > 0 && prev >= qty;
    const isFull = qty > 0 && n >= qty;
    const sizeDelta = (!wasFull && isFull) ? -1 : (wasFull && !isFull ? 1 : 0);

    if (baseStatus === "sold") {
      this.bumpKpi("soldOutUnits", -unitDelta);
      this.bumpKpi("replenTotalUnits", -unitDelta);
      this.bumpMoneyKpi("replenTotalValue", -unitDelta * price);
      if (sizeDelta) this.bumpKpi("sizesSoldOut", sizeDelta);
    } else if (baseStatus === "topup") {
      this.bumpKpi("belowUnits", -unitDelta);
      this.bumpKpi("replenTotalUnits", -unitDelta);
      this.bumpMoneyKpi("replenTotalValue", -unitDelta * price);
      if (sizeDelta) this.bumpKpi("sizesBelowTemplate", sizeDelta);
    }
  }

  retailPrice(box: HTMLElement): number {
    return parseFloat(box.getAttribute("data-retail-price")) || 0;
  }

  formatMoney(value: number): string {
    return this.currency + Math.round(value).toLocaleString("en-US");
  }

  // The money KPI keeps its unrounded total in data-raw, so repeated live bumps
  // stay exact while the text shows whole dollars.
  setMoneyKpi(id: string, value: any) {
    if (typeof value === "undefined" || value === null) return;
    const total = Number(value) || 0;
    this.kpiElements(id).forEach((el) => {
      el.setAttribute("data-raw", String(total));
      el.textContent = this.formatMoney(total);
    });
  }

  bumpMoneyKpi(id: string, delta: number) {
    if (!delta) return;
    this.kpiElements(id).forEach((el) => {
      const current = parseFloat(el.getAttribute("data-raw")) || 0;
      const next = Math.max(0, current + delta);
      el.setAttribute("data-raw", String(next));
      el.textContent = this.formatMoney(next);
    });
  }

  bumpKpi(id: string, delta: number) {
    this.kpiElements(id).forEach((el) => {
      const current = parseInt(el.textContent, 10) || 0;
      el.textContent = String(Math.max(0, current + delta));
    });
  }

  allPicked(container: HTMLElement): boolean {
    const boxes = container.querySelectorAll(".size-box");
    return boxes.length > 0 &&
      Array.prototype.every.call(boxes, (b: HTMLElement) => b.classList.contains("picked"));
  }

  refreshKpis(_data: any) {
    const needEl = document.getElementById("needingAttention");
    if (!needEl) return;

    const total = parseInt(needEl.getAttribute("data-styles-total"), 10) || 0;
    const fully = document.querySelectorAll(".card.replenished").length;
    needEl.textContent = String(Math.max(0, total - fully));
  }

  setStylesTotal(value: any) {
    if (typeof value === "undefined") return;
    const needEl = document.getElementById("needingAttention");
    if (needEl) needEl.setAttribute("data-styles-total", String(value));
  }

  remainingOf(box: HTMLElement): number {
    const qty = parseInt(box.getAttribute("data-qty"), 10) || 0;
    const picked = parseInt(box.getAttribute("data-picked-qty"), 10) || 0;
    return Math.max(0, qty - picked);
  }

  // The quantity is the units this action adds (the size's remaining need), so
  // marking a whole style/grab tops each size up to full without overshooting.
  picksFromBoxes(boxes: any) {
    return Array.prototype.map.call(boxes, (box: HTMLElement) => ({
      item_number: box.getAttribute("data-item-number"),
      ext_item_id: box.getAttribute("data-ext-item-id"),
      quantity: this.remainingOf(box)
    }));
  }

  postPicks(url: string, method: string, picks: any, withQuickGrab?: boolean) {
    const body: any = { picks: picks };
    if (withQuickGrab) body.with_quick_grab = true;
    return fetch(url + (window.location.search || ""), {
      method: method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json"
      },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then((r) => r.json());
  }

  // True if picking `box` would complete every size in `container`.
  wouldCompleteContainer(container: HTMLElement, box: HTMLElement): boolean {
    const boxes = container.querySelectorAll(".size-box");
    return boxes.length > 0 &&
      Array.prototype.every.call(boxes, (b: HTMLElement) => b === box || b.classList.contains("picked"));
  }

  // ---- Misc page controls ---------------------------------------------

  bindLoadMore() {
    const btn = document.getElementById("loadMoreBtn") as HTMLButtonElement;
    if (!btn) return;
    btn.addEventListener("click", () => {
      if (this.refilling) return;
      const original = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Loading…';
      this.fetchMoreActionable().then(() => {
        btn.disabled = false;
        btn.innerHTML = original;
        this.updateLoadMore();
      });
    });
  }

  // ---- Auto-refill -----------------------------------------------------

  // Keep the grid topped up with ~GRID_TARGET actionable cards.
  topUpGrid() {
    const actionable = document.querySelectorAll(".grid .card:not(.replenished)").length;
    if (actionable < this.GRID_TARGET) this.fetchMoreActionable();
  }

  fetchMoreActionable(): Promise<void> {
    if (this.refilling || !this.gridHasMore) return Promise.resolve();
    this.refilling = true;

    return fetch(this.stylesUrl + this.refillQuery(), { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      .then((r) => r.json())
      .then((data: any) => {
        this.refilling = false;
        this.gridHasMore = !!data.has_more;
        if (data.html) this.insertActionableCards(data.html);
        this.updateLoadMore();

        const actionable = document.querySelectorAll(".grid .card:not(.replenished)").length;
        if (actionable < this.GRID_TARGET && this.gridHasMore) return this.fetchMoreActionable();
      })
      .catch(() => { this.refilling = false; });
  }

  refillQuery(): string {
    const parts = this.gridExtItemIds().map((id) => "exclude[]=" + encodeURIComponent(id));
    const search = window.location.search.replace(/^\?/, "");
    if (search) parts.push(search);
    return "?" + parts.join("&");
  }

  gridExtItemIds(): string[] {
    const ids: string[] = [];
    Array.prototype.forEach.call(document.querySelectorAll(".grid .card"), (c: HTMLElement) => {
      const id = c.getAttribute("data-ext-item-id");
      if (id) ids.push(id);
    });
    const hero = document.getElementById("quickgrab");
    if (hero) { const id = hero.getAttribute("data-ext-item-id"); if (id) ids.push(id); }
    return ids;
  }

  // Insert new actionable cards above the replenished (grabbed) block.
  insertActionableCards(html: string) {
    const grid = document.getElementById("pickGrid");
    if (!grid) return;
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const firstReplenished = grid.querySelector(".card.replenished");
    Array.prototype.slice.call(temp.children).forEach((card: HTMLElement) => {
      if (firstReplenished) grid.insertBefore(card, firstReplenished);
      else grid.appendChild(card);
    });
  }

  updateLoadMore() {
    const wrap = document.getElementById("loadMoreWrap");
    if (wrap) (wrap as HTMLElement).style.display = this.gridHasMore ? "" : "none";
  }

  bindSendForm() {
    const form = document.getElementById("sendToHHForm") as HTMLFormElement;
    if (!form) return;
    const error = document.getElementById("sendError");

    Array.prototype.forEach.call(form.querySelectorAll('input[name="all"]'), (radio: HTMLElement) => {
      radio.addEventListener("change", () => {
        if (error) error.style.display = "none";
        this.updateSendButtonState();
      });
    });
    this.updateSendButtonState();

    form.addEventListener("submit", (e) => {
      if (error) error.style.display = "none";
      const mode = form.querySelector('input[name="all"]:checked') as HTMLInputElement;

      if (mode && mode.value === "selected") {
        const numbers = this.selectedItemNumbers();
        if (numbers.length === 0) {
          e.preventDefault();
          if (error) error.style.display = "block";
          return;
        }
        (form.querySelector('input[name="items"]') as HTMLInputElement).value = JSON.stringify(numbers);
      }
    });
  }

  bindModals() {
    const open = (id: string) => {
      const el = document.getElementById(id);
      if (el) el.classList.add("open");
    };

    const btnExport = document.getElementById("btnExport");
    if (btnExport) btnExport.addEventListener("click", () => open("exportModal"));

    const btnSend = document.getElementById("btnSend");
    if (btnSend) btnSend.addEventListener("click", () => {
      this.updateSelectedCount();
      this.updateSendButtonState();
      open("sendToHHModal");
    });

    const btnMode = document.getElementById("btnMode");
    if (btnMode) btnMode.addEventListener("click", () => open("modeModal"));

    const applyMode = document.getElementById("applyModeBtn") as HTMLButtonElement;
    if (applyMode) applyMode.addEventListener("click", () => this.saveMode(applyMode));

    const btnDisplay = document.getElementById("btnDisplaySettings");
    if (btnDisplay) btnDisplay.addEventListener("click", () => open("displaySettingsModal"));

    const applyDisplay = document.getElementById("applyDisplaySettingsBtn") as HTMLButtonElement;
    if (applyDisplay) applyDisplay.addEventListener("click", () => this.saveDisplaySettings(applyDisplay));

    Array.prototype.forEach.call(document.querySelectorAll(".modal-backdrop"), (backdrop: HTMLElement) => {
      backdrop.addEventListener("click", (e: any) => {
        if (e.target === backdrop || (e.target as HTMLElement).closest("[data-close]")) {
          backdrop.classList.remove("open");
        }
      });
    });
  }

  saveMode(btn: HTMLButtonElement) {
    const selected = document.querySelector('#modeModal input[name="pick_view_mode"]:checked') as HTMLInputElement;
    if (!selected || !this.updateModeUrl) return;

    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving…';

    fetch(this.updateModeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json"
      },
      credentials: "same-origin",
      body: JSON.stringify({ mode: selected.value })
    }).then((r) => {
      if (!r.ok) throw new Error("failed");
      window.location.reload();
    }).catch(() => {
      btn.disabled = false;
      btn.innerHTML = original;
      this.showToast("Couldn't change mode — please try again");
    });
  }

  saveDisplaySettings(btn: HTMLButtonElement) {
    if (!this.displaySettingsUrl) return;

    const checked = document.querySelectorAll('#displaySettingsModal input[name="pick_view_display"]:checked');
    const display = Array.prototype.map.call(checked, (cb: HTMLInputElement) => cb.value);

    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving…';

    fetch(this.displaySettingsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json"
      },
      credentials: "same-origin",
      body: JSON.stringify({ display: display })
    }).then((r) => {
      if (!r.ok) throw new Error("failed");
      window.location.reload();
    }).catch(() => {
      btn.disabled = false;
      btn.innerHTML = original;
      this.showToast("Couldn't save display settings — please try again");
    });
  }

  bindSortDropdown() {
    const wrap = document.getElementById("pickSort");
    if (!wrap) return;

    const trigger = document.getElementById("pickSortTrigger");
    const panel = document.getElementById("pickSortPanel");
    const hidden = document.getElementById("pickSortValue") as HTMLInputElement;
    const form = document.getElementById("pickFilterForm") as HTMLFormElement;

    const open = () => { panel.style.display = "block"; wrap.classList.add("open"); };
    const close = () => { panel.style.display = "none"; wrap.classList.remove("open"); };

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      if (wrap.classList.contains("open")) { close(); } else { open(); }
    });
    panel.addEventListener("click", (e) => e.stopPropagation());

    Array.prototype.forEach.call(wrap.querySelectorAll(".pick-sort-option"), (opt: HTMLElement) => {
      opt.addEventListener("click", () => {
        if (hidden) hidden.value = opt.getAttribute("data-value");
        if (form) form.submit();
      });
    });

    document.addEventListener("click", (e: any) => {
      if (wrap.classList.contains("open") && !wrap.contains(e.target)) close();
    });
  }

  bindCategoryFilter() {
    const pickCats = document.getElementById("pickCats");
    if (!pickCats) return;

    const trigger = document.getElementById("pickCatsTrigger");
    const panel = document.getElementById("pickCatsPanel");
    const search = document.getElementById("pickCatsSearch") as HTMLInputElement;
    const list = document.getElementById("pickCatsList");
    const empty = document.getElementById("pickCatsEmpty");
    const countEl = document.getElementById("pickCatsCount");
    const form = document.getElementById("pickFilterForm") as HTMLFormElement;

    const updateCount = () => {
      const n = list.querySelectorAll('input[type="checkbox"]:checked').length;
      countEl.textContent = String(n);
      countEl.style.display = n > 0 ? "" : "none";
    };
    const filterList = (term: string) => {
      term = (term || "").toLowerCase();
      let anyVisible = false;
      Array.prototype.forEach.call(list.querySelectorAll(".pick-cats-row"), (row: HTMLElement) => {
        const match = row.getAttribute("data-name").indexOf(term) !== -1;
        row.classList.toggle("hidden", !match);
        if (match) anyVisible = true;
      });
      empty.style.display = anyVisible ? "none" : "block";
    };
    const openPanel = () => {
      panel.style.display = "block";
      pickCats.classList.add("open");
      search.value = "";
      filterList("");
      search.focus();
    };
    const closePanel = () => {
      panel.style.display = "none";
      pickCats.classList.remove("open");
    };

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      if (pickCats.classList.contains("open")) { closePanel(); } else { openPanel(); }
    });
    panel.addEventListener("click", (e) => e.stopPropagation());
    search.addEventListener("input", () => filterList(search.value));
    list.addEventListener("change", updateCount);

    document.getElementById("pickCatsApply").addEventListener("click", () => { if (form) form.submit(); });
    document.getElementById("pickCatsClear").addEventListener("click", () => {
      Array.prototype.forEach.call(
        list.querySelectorAll('input[type="checkbox"]:checked'),
        (cb: HTMLInputElement) => { cb.checked = false; }
      );
      updateCount();
      if (form) form.submit();
    });

    document.addEventListener("click", (e: any) => {
      if (pickCats.classList.contains("open") && !pickCats.contains(e.target)) {
        closePanel();
      }
    });
  }
}

export function LoadPage() {
  InitPageStrict("replenishment_pick_views index", () => { new ReplenishmentPickView(); });
}
